/**
 * Centering, and what it is worth.
 *
 * Two rectangles: the outer edge of the card, and the inner edge of its border
 * — where the frame stops and the picture starts. The four gaps between them
 * are the borders, and centering is how evenly each opposing pair splits.
 *
 * All of it is arithmetic on numbers the person dragged, so it lives apart from
 * the dragging and can be checked on its own.
 */

/* PSA's published tolerances, as the larger of the two sides. 55 means 55/45.
 *
 * These are the whole output — every verdict this screen gives comes from this
 * table and nothing else — so they are in one place, named, and easy to change
 * when the standards move. Worth checking against PSA's current grading
 * standards rather than trusting them here: PSA states Gem Mint as "55/45 to
 * 60/40 or better" on the front, which is a range, and this takes the strict
 * end of it.
 */
export const PSA_TOLERANCE = {
  front: [
    { grade: 10, label: "Gem Mint", max: 55 },
    { grade: 9, label: "Mint", max: 60 },
    { grade: 8, label: "NM-MT", max: 65 },
    { grade: 7, label: "NM", max: 70 },
    { grade: 6, label: "EX-MT", max: 75 },
  ],
  // The back is judged far more loosely, which is why a card that looks off on
  // the reverse can still gem.
  back: [
    { grade: 10, label: "Gem Mint", max: 75 },
    { grade: 9, label: "Mint", max: 90 },
    { grade: 8, label: "NM-MT", max: 90 },
    { grade: 7, label: "NM", max: 90 },
    { grade: 6, label: "EX-MT", max: 90 },
  ],
};

/**
 * One axis: two opposing borders, as whole percentages that add to 100.
 *
 * Whole numbers, and the second derived from the first rather than rounded on
 * its own — rounding both independently printed "61/40", which adds to 101 and
 * reads as a broken tool.
 *
 * Rounded here rather than at the point of display, so the grade is decided on
 * the number you can see. Judging the unrounded value instead put "60/40" on
 * screen next to a verdict of PSA 8, when 60/40 is exactly the PSA 9 line —
 * true, off a hidden 60.4, and indistinguishable from a bug. A tenth of a
 * point is noise from a hand-placed box anyway.
 */
export function axisSplit(a, b) {
  const near = Number(a);
  const far = Number(b);
  if (!Number.isFinite(near) || !Number.isFinite(far)) return null;
  if (near < 0 || far < 0) return null;
  const total = near + far;
  if (total <= 0) return null;
  const first = Math.round((near / total) * 100);
  const second = 100 - first;
  return { first, second, worst: Math.max(first, second) };
}

/**
 * The four borders, from two rectangles given as {x, y, w, h} in any single
 * unit — the ratios are scale-free, so pixels on screen are as good as
 * millimetres, and no calibration is needed.
 */
export function borders(outer, inner) {
  if (!outer || !inner) return null;
  const left = inner.x - outer.x;
  const right = outer.x + outer.w - (inner.x + inner.w);
  const top = inner.y - outer.y;
  const bottom = outer.y + outer.h - (inner.y + inner.h);
  if ([left, right, top, bottom].some((v) => !Number.isFinite(v) || v < 0)) return null;
  return { left, right, top, bottom };
}

/** The grade centering alone allows. Centering is a ceiling, never a promise:
 *  it cannot see a soft corner, a scratch, or print lines. */
export function gradeCeiling(worstPercent, side = "front") {
  const table = PSA_TOLERANCE[side] || PSA_TOLERANCE.front;
  const hit = table.find((t) => worstPercent <= t.max + 1e-9);
  return hit || null;
}

/**
 * Everything the screen shows, from the two boxes.
 *
 * `side` picks which tolerance table to judge against; the measurement itself
 * is the same either way.
 */
export function measure(outer, inner, side = "front") {
  const b = borders(outer, inner);
  if (!b) return null;

  const lr = axisSplit(b.left, b.right);
  const tb = axisSplit(b.top, b.bottom);
  if (!lr || !tb) return null;

  // The worse axis is the one that decides it: a card perfect side to side and
  // badly off top to bottom is a badly centred card.
  const worst = Math.max(lr.worst, tb.worst);
  return {
    borders: b,
    lr,
    tb,
    worst,
    ceiling: gradeCeiling(worst, side),
    side,
  };
}

/** "55/45" — the way centering is written and spoken. */
export const asRatio = (split) => (split ? `${split.first}/${split.second}` : "—");
